import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { getDateTimeFromString, validateDate } from '@/utils/date.ts'
import { useEffect, useState } from 'react'

interface DateTimePickerProps {
  id: string
  timestamp: number
  setTimestamp: (timestamp: number) => void
  validator?: (timestamp: number) => void
}

export function DateTimePicker({ id, timestamp, setTimestamp, validator }: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [dateInput, setDateInput] = useState('')
  const [timeInput, setTimeInput] = useState('')
  const [tempTimestamp, setTempTimestamp] = useState(timestamp)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const date = new Date(timestamp)
    setDateInput(formatDate(date))
    setTimeInput(formatTime(date))
    setTempTimestamp(timestamp)
  }, [timestamp])

  const updateTempTimestamp = (dateStr: string, timeStr: string) => {
    try {
      setError(null)
      validateDate(dateStr, timeStr)
      const { date } = getDateTimeFromString(dateStr, timeStr)
      setTempTimestamp(date.getTime())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid date or time')
    }
  }

  useEffect(() => {
    try {
      validateDate(dateInput, timeInput)
      validator?.(tempTimestamp)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Invalid date or time')
    }
  }, [dateInput, timeInput, validator, tempTimestamp])

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open && error) {
          setTempTimestamp(timestamp)
          setDateInput(formatDate(new Date(timestamp)))
          setTimeInput(formatTime(new Date(timestamp)))
          setError(null)
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button size="icon" variant="ghost" className={cn('', !timestamp && 'text-muted-foreground')}>
          <img src="/images/calendar-navy-icon.svg" alt="picker" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="p-0" align="start">
        <Calendar
          id={id}
          mode="single"
          selected={new Date(tempTimestamp)}
          onSelect={(selectedDate) => {
            if (selectedDate) {
              const newDate = new Date(selectedDate)
              const [hours, minutes] = timeInput.split(':').map(Number)
              newDate.setHours(hours || 0)
              newDate.setMinutes(minutes || 0)
              setTempTimestamp(newDate.getTime())
              setDateInput(formatDate(newDate))
            }
          }}
          initialFocus
        />

        <div className="p-3 grid grid-cols-2 gap-2 w-full">
          <Input
            type="text"
            value={dateInput}
            onChange={(e) => {
              setDateInput(e.target.value)
              updateTempTimestamp(e.target.value, timeInput)
            }}
            placeholder="YYYY/MM/DD"
          />
          <Input
            type="text"
            value={timeInput}
            onChange={(e) => {
              setTimeInput(e.target.value)
              updateTempTimestamp(dateInput, e.target.value)
            }}
            placeholder="HH:mm"
          />
        </div>
        <div className="p-3">{error && <p className="text-red-500">{error}</p>}</div>
        <div className="p-3 flex justify-end">
          <Button
            disabled={error !== null}
            onClick={() => {
              setTimestamp(tempTimestamp)
              setIsOpen(false)
            }}
          >
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

const formatDate = (date: Date) =>
  `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`

const formatTime = (date: Date) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
